import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SettingsIcon, WalletIcon, PanelLeft } from 'lucide-react';
import { MenuItemType } from './VerticalNavbar';

interface TopNavBarProps {
  navItems: MenuItemType[];
  activeKey?: string;
  pathname: string;
  credits: number;
  onDockLeft: () => void;
  onShowCreditsModal: () => void;
}

export const HorizontalNavbar: React.FC<TopNavBarProps> = ({
  navItems,
  activeKey,
  pathname,
  credits,
  onDockLeft,
  onShowCreditsModal,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="gradient-surface mt-4 hidden w-full items-center justify-between rounded-full px-6 py-2 md:mb-5 md:flex md:px-8">
      <div className="flex items-start md:gap-3 ">
        {navItems.map(item => {
          const isActive = activeKey
            ? item.key === activeKey
            : pathname === item.route || (item.key === 'home' && pathname === '/');

          return (
            <Link
              key={item.key}
              to={item.route}
              className={`relative flex items-center gap-2 rounded-full px-3 py-1.5 font-ambit transition-colors ${
                isActive
                  ? 'font-bold text-primary'
                  : 'text-secondary/70 hover:bg-secondary/5 hover:text-secondary'
              }`}
              aria-label={item.label ?? item.key}
              title={item.label}
            >
              {item.icon && (
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center ${isActive ? 'text-primary' : 'text-secondary/55'}`}
                >
                  {item.icon}
                </span>
              )}
              {item.key !== 'home' && item.label && <span>{item.label}</span>}
              {item.badge !== undefined && item.badge > 0 && (
                <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-warning px-1 text-xs text-lightest">
                  {item.badge}
                </span>
              )}
            </Link>
          );
        })}
      </div>

      <div className="ml-auto flex items-center gap-3">
        <button
          className="flex items-center gap-2 rounded-full border border-primary/20 px-3 py-1 text-sm text-secondary transition-colors hover:bg-secondary/5"
          title={t('navBar.credits')}
          onClick={onShowCreditsModal}
        >
          <WalletIcon className="h-4 w-4" />
          <span className="font-bold">{credits}</span>
        </button>

        <Link
          to="/settings"
          className="rounded-full p-2 text-secondary/70 transition-colors hover:bg-secondary/5 hover:text-secondary"
          aria-label={t('navBar.settings')}
          title={t('navBar.settings')}
        >
          <SettingsIcon className="h-4 w-4" />
        </Link>

        <button
          type="button"
          onClick={onDockLeft}
          className="rounded-full p-2 text-secondary/70 transition-colors hover:bg-secondary/5 hover:text-secondary"
          aria-label="Dock left"
          title={t('navBar.dockLeft')}
        >
          <PanelLeft className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
